import React, { useEffect, useState } from 'react';
import { Box, Button, CircularProgress, Link, Typography } from '@mui/material';
import { Link as ReactRouterLink } from 'react-router-dom';
import { useUser } from '../../../../contexts/UserContext';
import { useNetwork } from '../../../../utils/network';
import { useShowSnackbar } from '../../../../utils/snackbar';
import { format } from 'date-fns';

export const SettingsUserSubscription = () => {
  const { user, setUser, userCurrentSubscription, setUserCurrentSubscription } =
    useUser();
  const { get, put } = useNetwork();
  const { showSnackbar } = useShowSnackbar();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const getCurrentSubscription = async () => {
      try {
        const result = await get('user/subscription', true);
        setUserCurrentSubscription({
          package: result.package,
          endDate: result.endDate
        });
      } catch (error: any) {
        console.log(error);
      }
    };

    getCurrentSubscription();
  }, []);

  /* Cancelling used to be silent in BOTH directions: on success setLoading(false) was never
     called, so the button just sat spinning; on failure the only trace was a console.error.
     Either way the member was left staring at a screen that told them nothing, which is how
     someone ends up believing they cancelled and then getting charged. A failure here now says
     so loudly, because the consequence of assuming it worked is a real charge. */
  const handleCancelSubscription = async () => {
    setLoading(true);
    try {
      const result = await put('/user/subscription', {}, true);

      setUserCurrentSubscription({
        ...userCurrentSubscription,
        endDate: result.endDate
      });
      showSnackbar(
        result?.endDate
          ? `Subscription cancelled. You keep your benefits until ${format(
              new Date(result.endDate),
              'd MMMM yyyy'
            )}, and it will not renew.`
          : 'Subscription cancelled — it will not renew.',
        'success'
      );
    } catch (error: any) {
      console.error('Error cancelling subscription:', error);
      showSnackbar(
        error?.message ||
          'We could not cancel your subscription. Please contact support so you are not charged again.',
        'error'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      {userCurrentSubscription.package !== null ? (
        <Box sx={{ p: '2rem' }}>
          {/* Smaller on phones, and allowed to break as a last resort: at the
              h3 size "Subscription" is one word wider than a 320px phone leaves
              inside the settings panel, which clipped it mid-word. */}
          <Typography
            variant="h3"
            fontWeight="bold"
            textAlign="center"
            sx={{
              pb: '2rem',
              fontSize: { xs: '1.75rem', sm: '3rem' },
              overflowWrap: 'anywhere'
            }}
          >
            Current Subscription
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <Box
              component="img"
              height="450px"
              src={userCurrentSubscription.package.imageUrl}
            />
          </Box>

          {userCurrentSubscription.endDate === null ? (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                flexDirection: 'column',
                mt: '2rem'
              }}
            >
              <Typography
                textAlign="center"
                variant="h6"
                fontWeight="bold"
                sx={{
                  pb: '2rem',
                  px: '2rem'
                }}
              >
                By cancelling your subscription, you will lose all stacking
                entries gained for giveaways. You will also be unable to
                subscribe again until your current subscription period has
                ended.
              </Typography>
              <Button
                variant="contained"
                onClick={handleCancelSubscription}
                disabled={loading}
                startIcon={loading ? <CircularProgress size={20} /> : null}
              >
                {loading ? 'Canceling...' : 'Cancel Subscription'}
              </Button>
            </Box>
          ) : (
            <Box>
              <Typography
                textAlign="center"
                variant="h6"
                fontWeight="bold"
                sx={{
                  pt: '2rem',
                  px: '2rem'
                }}
              >
                Subscription Cancelled, active until{' '}
                {format(
                  new Date(userCurrentSubscription.endDate),
                  'MMMM d, yyyy'
                )}
              </Typography>
            </Box>
          )}
        </Box>
      ) : (
        <Box sx={{ p: '2rem' }}>
          <Typography
            variant="h3"
            fontWeight="bold"
            textAlign="center"
            sx={{
              pb: '2rem',
              fontSize: { xs: '1.75rem', sm: '3rem' },
              overflowWrap: 'anywhere'
            }}
          >
            No active subscription
          </Typography>
          <Typography variant="body1" textAlign="center">
            Receive benefits such as monthly entries to giveaways and more by
            subscribing
          </Typography>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              mt: '2rem'
            }}
          >
            <Link
              component={ReactRouterLink}
              to="/member-portal/subscription"
              variant="body2"
            >
              <Button
                variant="contained"
                sx={{
                  fontWeight: 800,
                  backgroundColor: '#38e8ff',
                  color: 'white'
                }}
              >
                Check subscription options
              </Button>
            </Link>
          </Box>
        </Box>
      )}
    </Box>
  );
};
